import { useEffect, useState } from "react";
import { useFormik } from 'formik';
import * as Yup from "yup";
import { Box, Button, TextField, Autocomplete, Switch, Checkbox, FormControlLabel, FormGroup, Typography, Divider } from "@mui/material";
import { CustomNumberInput, Modal } from '../../components';
import { FromArticuloSkeleton } from '../../components/skeletons';
import CategoriaService from '../../services/categorias';
import CustomerService from '../../services/customers';
import ImagenesService from '../../services/imagenes';
import ImagenesAIService from '../../services/imagenesAI';
import UploadImage from "../UploadImage";
import { Root } from '../../styles';
import { FormPrecio } from "../FormPrecio";
import { OPERATION } from "../../helpers/enums";
import { Add } from "@mui/icons-material";
import SelectArticles from "./SelectArticles";
import { ccyFormat } from "../../helpers/helper";

const formSchema = Yup.object().shape({
  name: Yup.string().required("Campo Requerido"),
  description: Yup.string().required("Campo Requerido"),
  //sku: Yup.string().required("Campo Requerido"),
  category_id: Yup.string().required("Campo Requerido"),
  customer_id: Yup.string().required("Campo Requerido"),
  quantity: Yup.number().required("Campo Requerido"),
  active: Yup.bool(),
  news: Yup.bool(),
  to: Yup.string().required("Debe seleccionar uno"),
  prices: Yup.object().shape({
    list_price: Yup.number().required('Campo Requerido').moreThan(0),
    sale_price: Yup.number().required('Campo Requerido').moreThan(0),
  }),
  asChilds: Yup.bool(),
  childs: Yup.array()
    .when("asChilds", {
      is: true,
      then: (schema) => schema.min(1, "Debe haber al menos un elemento en el array").required("El array es requerido"),
      otherwise: (schema) => schema.optional(),
    }),
  // investment: Yup.number()
  //   .when(
  //     'news', {
  //     is: true,
  //     then: () => Yup.number()
  //       .moreThan(0, 'El valor debe ser mayor que 0')
  //       .test(
  //         'investment-test',
  //         'El investment debe ser mayor que list_price * quantity',
  //         function (value) {
  //           const { prices, quantity } = this.parent;
  //           const listPrice = prices?.list_price;
  //           const qty = quantity;

  //           // Verifica que listPrice y qty no sean undefined o null
  //           if (listPrice != null && qty != null) {
  //             return value > listPrice * qty;
  //           }
  //           return true; // Si no hay valores para comparar, pasa la validación
  //         }
  //       ),
  //     otherwise: () => Yup.number()
  //   }
  //   )
})

const initialValues = {
  name: "",
  description: "",
  sku: "",
  category_id: "",
  customer_id: "",
  quantity: 0,
  active: true,
  news: false,
  to: "",
  prices: {
    list_price: 0,
    sale_price: 0
  },
  investment: 0,
  asChilds: false,
  childs: [],
  web: true
}

const buildInitialValues = (data = {}) => ({
  ...initialValues,
  ...data,
  prices: {
    ...initialValues.prices,
    ...(data?.prices || {})
  },
  web: data?.web ?? true
})

const FormArticulo = ({
  articleData = initialValues,
  onClose,
  onSubmit,
  saving = false,
  withImages = true,
  pricesInModal = true,
  onChangeData,
  sx,
  createArticle = false,
  articles,
}) => {
  const [images, setImages] = useState([]);
  const [imagePrompt, setImagePrompt] = useState('');
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [article, setArticle] = useState(buildInitialValues(articleData));

  useEffect(() => {
    setArticle(buildInitialValues(articleData));
  }, [articleData]);

  const loadCategories = async () => {
    var resp = await CategoriaService.getAll()
    setCategories(resp);
  }

  const loadCustomers = async () => {
    var resp = await CustomerService.getAll()
    setCustomers(resp);
  }

  useEffect(() => {
    if (categories && categories.length === 0) {
      loadCategories()
    }
  }, [categories])

  useEffect(() => {
    if (customers && customers.length === 0) {
      loadCustomers()
    }
  }, [customers])

  const handleSubmitForm = async (data) => {
    console.log(data)
    console.log("Submit")
    const formData = new FormData();
    images.forEach((image, index) => {
      formData.append(`image`, image);
    });
    let res;
    if (imagePrompt.trim()) {
      formData.append('prompt', imagePrompt.trim());
      res = await ImagenesAIService.generate(formData);
    } else {
      res = await ImagenesService.upload(formData);
    }
    console.log(res)
    data.images = res
    if (onSubmit) {
      onSubmit(data)
    }
  }

  const onImageSelected = (images) => {
    console.log(images)
    setImages(images)
  }

  const { handleChange, handleSubmit, setFieldValue, values, errors, isValid } = useFormik({
    initialValues: article,
    onSubmit: handleSubmitForm,
    validationSchema: formSchema,
    validateOnMount: true
  })

  const { article: { name, description, category_id, sku, customer_id } = {}, quantity, active, news, web, to, prices, investment, asChilds, childs } = values

  useEffect(() => {
    setArticle(values)
    if (onChangeData) {
      onChangeData(values, isValid)
    }
  }, [values, isValid, onChangeData]);

  const handleOnSave = articles => {
    setFieldValue("childs", articles);
    setFieldValue("asChilds", articles.length > 0);
    setFieldValue("quantity", Math.min(...articles.map(a => a.quantity)));
    setFieldValue("prices.list_price", articles.reduce((acc, item) => acc + (item.prices?.list_price), 0));
    setOpenModal(false)
  }

  const operationText = (operation) => operation === OPERATION.PEDIR ? "Para pedir o comprar" : operation === OPERATION.VENDER ? "Disponible para venta" : ""

  return <>
    <Box
      component="form"
      onSubmit={handleSubmit}
      display='flex'
      flexDirection='column'
      sx={sx}
      paddingX={3}
    >
      {saving ? <FromArticuloSkeleton /> :
        <Box>
          <Box display='flex' flexDirection='row' justifyContent="space-between" marginY={1}>
            {createArticle ? <strong>Crear Articulo</strong> : <strong>Nuevo Articulo</strong>}
            <Switch
              slots={{
                root: Root,
              }}
              slotProps={{
                input: { 'aria-label': 'Demo switch' }
              }}
              id='active'
              value={active}
              onChange={handleChange}
              defaultChecked
            />
          </Box >
          <TextField
            id="investment"
            label="Dinero disponible"
            type="text"
            variant="standard"
            disabled
            fullWidth
            error={errors.investment}
            helperText={errors.investment}
            value={investment}
            onChange={handleChange}
          />
          <Autocomplete
            options={categories}
            getOptionLabel={option => option.name}
            clearOnEscape
            sx={{ marginY: 2, width: 235 }}
            onChange={(event, newValue) => {
              setFieldValue('category_id', newValue?._id || null);
            }}
            renderInput={(params) =>
              <TextField
                {...params}
                id="category_id"
                label="Categoria"
                type="text"
                variant="standard"
                error={errors.category_id}
                helperText={errors.category_id}
                value={category_id}
              />
            }
          />
          <Autocomplete
            options={customers}
            getOptionLabel={option => option.name}
            clearOnEscape
            sx={{ marginBottom: 2, width: 235 }}
            onChange={(event, newValue) => {
              setFieldValue('customer_id', newValue?._id || null);
            }}
            renderInput={(params) =>
              <TextField
                {...params}
                id="customer_id"
                label="Proveedor"
                type="text"
                variant="standard"
                error={errors.customer_id}
                helperText={errors.customer_id}
                value={customer_id}
              />
            }
          />
          <Box display='flex' flexDirection='row' gap={2} marginY={1}>
            <TextField
              id="name"
              label="Nombre"
              type="text"
              variant="outlined"
              fullWidth
              error={errors.name}
              helperText={errors.name}
              //name="name"
              value={name}
              onChange={handleChange}
            />
            <CustomNumberInput
              id="quantity"
              label='Cantidad'
              minimo={0}
              maximo={200}
              value={quantity}
              onChange={(event, newValue) => {
                event.preventDefault();
                setFieldValue('quantity', newValue);
              }}
              incrementsButtons
              disabled={asChilds}
            />
          </Box>
          <TextField
            id="description"
            label="Descripción"
            type="text"
            variant="outlined"
            fullWidth
            error={errors.description}
            helperText={errors.description}
            value={description}
            onChange={handleChange}
            multiline
            maxRows={5}
            sx={{ marginY: 1 }}
          />
          <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center'>
            {(sku && sku !== "") && <TextField
              id="sku"
              label="Codigo"
              type="text"
              variant="outlined"
              sx={{ maxWidth: '50%', marginY: 1 }}
              error={errors.sku}
              helperText={errors.sku}
              value={sku}
              onChange={handleChange}
            />}
            {pricesInModal && <Button variant="outlined" onClick={() => setOpenModal(true)}>
              Precios
            </Button>}
          </Box>
          <Box display='flex' flexDirection='column'>
            {createArticle && <Button startIcon={<Add />} onClick={() => setOpenModal(true)}>Seleccionar Articulos</Button>}
            {createArticle && asChilds && <Box>
              {childs?.map(child => <Box display='flex' flexDirection='row' justifyContent='space-between'>
                <Typography variant='caption'>{child.quantity}</Typography>
                <Typography variant='caption'>{child.article.name}</Typography>
                <Typography variant='caption'>{`$ ${ccyFormat(child.prices.sale_price)}`}</Typography>
              </Box>)}
              <Divider />
              <Box display='flex' justifyContent='flex-end'>
                <Typography variant='caption' component='span'>{`$ ${ccyFormat(childs ? childs.reduce((acc, item) => acc + (item.prices?.sale_price), 0) : 0)}`}</Typography>
              </Box>
            </Box>}
          </Box>
          {!pricesInModal && <FormPrecio buttons={false} onClose={() => setOpenModal(false)} prices={prices} onChange={(field, newValue) => {
            setFieldValue(field, newValue);
          }} />}
          {withImages && <UploadImage onImagesSelected={onImageSelected} uploadImage />}
          {withImages && <TextField
            size='small'
            fullWidth
            placeholder='Prompt IA para imágenes (opcional)'
            value={imagePrompt}
            onChange={(e) => setImagePrompt(e.target.value)}
            sx={{ mt: 0.75 }}
          />}
          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            Content={() => <SelectArticles
              articles={articles}
              onClose={() => setOpenModal(false)}
              onSave={handleOnSave}
              artsSelected={childs}
            />
            }
          />
        </Box>}
      {pricesInModal && <Box sx={{ mt: 5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button
          type="submit"
          variant="outlined"
          onClick={onClose}
        >Cancelar</Button>
        <Button
          type="submit"
          variant="outlined"
          disabled={!isValid}
        >Guardar</Button>
      </Box>}
      {pricesInModal && <Modal open={openModal} onClose={() => setOpenModal(false)} Content={() => <FormPrecio onClose={() => setOpenModal(false)} prices={prices} onChange={(field, newValue) => {
        setFieldValue(field, newValue);
      }} />} />}
      <FormGroup >
        {<FormControlLabel disabled={createArticle} control={<Checkbox id='web' checked={!!web} onChange={(event, newValue) => {
          setFieldValue('web', newValue);
        }} />} label="Disponible en web?" />}
        {<FormControlLabel disabled={createArticle} control={<Checkbox id='news' checked={news} onChange={(event, newValue) => {
          setFieldValue('news', newValue);
        }} />} label="Ingreso nuevo?" />}
        {Object.values(OPERATION).map(operation => <FormControlLabel control={
          <Checkbox
            id='news'
            disabled={createArticle}
            checked={!createArticle ? operation === to : operation === OPERATION.VENDER}
            onChange={(event, newValue) => {
              setFieldValue('to', newValue ? operation : "");
            }} />}
          label={operationText(operation)}
        />)}
      </FormGroup >
    </Box>
  </>
}

export default FormArticulo;